import React from "react";

const BETTER_ME_HELP_STEPS = [
  {
    title: "Shape pitch",
    body: "Drag the purple nodes up or down. The dashed violet curve shows the next edit before it is applied.",
    badge: "Purple nodes",
  },
  {
    title: "Choose an area",
    body: "Drag the orange A and B vertical lines to select the sound or phrase you want to hear, stretch, or compress.",
    badge: "A/B lines",
  },
  {
    title: "Stretch duration",
    body: "Choose a duration factor from 0.3× to 3×, then click Mark stretch. Separate or nested regions are allowed; partial overlaps are blocked.",
    badge: "Duration",
  },
  {
    title: "Try Better Me",
    body: "Try Better Me sends the edit to the backend, creates a temporary edited voice, and updates this editor with the new curve.",
    badge: "Preview",
  },
  {
    title: "Compare",
    body: "Play original always plays the first recording. Play edited plays the latest Better Me version.",
    badge: "Compare",
  },
  {
    title: "Save",
    body: "Save Better Me accepts the latest edited audio. Until you save, it remains only a preview.",
    badge: "Final",
  },
];

const BETTER_ME_REFERENCE = {
  title: "“A better me”: Using acoustically modified learner voices as models",
  authors: "Alice J. Henderson & Radek Skarnitzl",
  year: "2022",
  journal: "Language Learning & Technology",
  url: "https://www.lltjournal.org/item/10125-73462/",
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function HelpStepCard({ step, index }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white shadow-sm">
          {index + 1}
        </div>

        <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-violet-700">
          {step.badge}
        </span>
      </div>

      <div className="text-base font-black text-slate-950">
        {step.title}
      </div>

      <div className="mt-2 text-sm font-semibold leading-6 text-slate-600">
        {step.body}
      </div>
    </div>
  );
}

function LegendCard({ color, title, text }) {
  const styles = {
    blue: {
      wrapper: "border-blue-200 bg-blue-50",
      label: "text-blue-700",
      body: "text-blue-950",
    },
    violet: {
      wrapper: "border-violet-200 bg-violet-50",
      label: "text-violet-700",
      body: "text-violet-950",
    },
    orange: {
      wrapper: "border-orange-200 bg-orange-50",
      label: "text-orange-700",
      body: "text-orange-950",
    },
  };

  const style = styles[color];

  return (
    <div className={cx("rounded-2xl border px-4 py-3", style.wrapper)}>
      <div className={cx("text-xs font-black uppercase tracking-[0.14em]", style.label)}>
        {title}
      </div>

      <div className={cx("mt-1 text-sm font-bold", style.body)}>
        {text}
      </div>
    </div>
  );
}

export default function BetterMeHelpModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="better-me-help-title"
    >
      <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-[32px] border border-white/30 bg-white shadow-2xl">
        <div className="border-b border-slate-200 bg-gradient-to-br from-violet-50 via-white to-blue-50 px-5 py-5 sm:px-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex items-center rounded-full border border-violet-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-violet-700 shadow-sm">
                Better Me help
              </div>

              <h2
                id="better-me-help-title"
                className="text-2xl font-black tracking-tight text-slate-950"
              >
                How to edit the learner voice
              </h2>

              <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-600">
                Use pitch nodes for melody and A/B lines for duration regions.
                Try edits freely; only Save Better Me accepts the final version.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-xl font-black text-slate-500 shadow-sm transition hover:-translate-y-0.5 hover:text-slate-950"
              aria-label="Close help"
            >
              ×
            </button>
          </div>
        </div>

        <div className="max-h-[68vh] overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
          <div className="grid gap-4 md:grid-cols-2">
            {BETTER_ME_HELP_STEPS.map((step, index) => (
              <HelpStepCard
                key={step.title}
                step={step}
                index={index}
              />
            ))}
          </div>

          <div className="mt-5 rounded-[24px] border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-black text-amber-950">
              Practical rule
            </div>

            <div className="mt-2 text-sm font-semibold leading-6 text-amber-900">
              Use broad duration edits for phrase tempo and smaller nested edits
              for vowels or important sounds. Example: whole phrase 1.25× plus
              one vowel 1.5× makes that vowel effectively longer.
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <LegendCard
              color="blue"
              title="Blue"
              text="Original recording"
            />

            <LegendCard
              color="violet"
              title="Violet"
              text="Edited Better Me"
            />

            <LegendCard
              color="orange"
              title="Orange"
              text="A/B selected area"
            />
          </div>

          <div className="mt-5 rounded-[24px] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-blue-50 p-4">
            <div className="mb-2 inline-flex items-center rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-violet-700 shadow-sm">
              Acknowledgement
            </div>

            <div className="text-sm font-black text-slate-950">
              Inspired by the “Better Me” approach
            </div>

            <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
              This feature follows the same general pedagogical idea: learners
              may benefit from hearing an acoustically modified version of their
              own voice as a model. The implementation here is adapted for
              interactive pitch and duration editing inside Catchphraser.
            </p>

            <a
              href={BETTER_ME_REFERENCE.url}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex rounded-2xl border border-violet-300 bg-white px-4 py-2 text-sm font-black text-violet-800 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-500 hover:bg-violet-50"
            >
              Read: {BETTER_ME_REFERENCE.authors} ({BETTER_ME_REFERENCE.year})
            </a>

            <div className="mt-2 text-xs font-semibold leading-5 text-slate-500">
              {BETTER_ME_REFERENCE.title}. {BETTER_ME_REFERENCE.journal}.
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-7">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs font-bold leading-5 text-slate-500">
              Tip: first make a small edit, click Try Better Me, then continue
              from the updated curve.
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-900 bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}